import { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components/macro";
import { SudoButton } from "src/sudokit/components/SudoButton";

type Props = {
  entityLabel: string;
  addToCurrentLabel: string;
  saving: boolean;
  initialAddToCurrent: boolean;
  onSave: (addToCurrent: boolean) => void;
  onDontSave: () => void;
  onCancel: () => void;
};

export default function UnsavedChangesConfirm({ entityLabel, addToCurrentLabel, saving, initialAddToCurrent, onSave, onDontSave, onCancel }: Props) {
  const [addToCurrent, setAddToCurrent] = useState(initialAddToCurrent);

  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const handleBackdrop = () => {
    if (saving) return;
    onCancel();
  };

  return createPortal(
    <Backdrop onMouseDown={handleBackdrop}>
      <Dialog
        onMouseDown={stop}
        onClick={stop}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-changes-title"
      >
        <h2 id="unsaved-changes-title">Save changes?</h2>
        <p>You made changes to the traits for {entityLabel}.</p>
        <label className="add-to-current">
          <input
            type="checkbox"
            checked={addToCurrent}
            onChange={(e) => setAddToCurrent(e.target.checked)}
          />
          <span>{addToCurrentLabel}</span>
        </label>
        <div className="actions">
          <SudoButton
            variant="secondary"
            onClick={onDontSave}
            disabled={saving}
          >
            Don't Save
          </SudoButton>
          <SudoButton
            variant="primary"
            onClick={() => onSave(addToCurrent)}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save New Defaults"}
          </SudoButton>
        </div>
      </Dialog>
    </Backdrop>,
    document.body,
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 40100;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Dialog = styled.div`
  background-color: var(--menu-color);
  border-radius: 16px;
  padding: 24px;
  width: min(420px, calc(100vw - 32px));
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1), 0px 0px 2px 0px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: var(--grey-0);

  h2 {
    font-size: 20px;
    font-weight: 500;
    margin: 0;
    color: var(--grey-0);
  }

  p {
    margin: 0;
    color: var(--grey-1);
    font-size: 15px;
    line-height: 1.4;
  }

  .add-to-current {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
    color: var(--grey-0);
    font-size: 15px;
    padding: 4px 0;

    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      accent-color: var(--primary-cta);
      cursor: pointer;
    }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 8px;
  }
`;
